import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authApi } from '../services/api';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';
import { LOGO_URL } from '../constants/brand';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [logoError, setLogoError] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await authApi.verifyEmail(token);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      toast.success('Email verified successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Verification failed';
      setMessage(errorMessage);

      if (errorMessage.includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }

      toast.error(errorMessage);
    }
  };

  const handleResend = async () => {
    // Extract email from token or ask user
    // For now, redirect to login with message
    navigate('/login?resend=true');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo from URL */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 p-1">
            {!logoError ? (
              <img src={LOGO_URL} alt="One Globe Alliance" className="w-full h-full object-contain" onError={() => setLogoError(true)} />
            ) : (
              <Globe className="w-10 h-10 text-blue-600" aria-hidden />
            )}
          </div>
          <h1 className="text-3xl font-bold text-white">One Globe Alliance</h1>
          <p className="text-blue-200 mt-2">Members Portal</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-xl text-center">Email Verification</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {status === 'loading' && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Verifying your email address...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Verified!</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <p className="text-sm text-gray-500">Redirecting to login...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification Failed</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button onClick={() => navigate('/login')} className="w-full">
                  Go to Login
                </Button>
              </div>
            )}

            {status === 'expired' && (
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Link Expired</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button onClick={handleResend} className="w-full mb-2">
                  Request New Verification Link
                </Button>
                <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
                  Go to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
